import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@rekati/utils";

export type RimTone = "paint" | "success" | "danger";

/* Cult UI's halo blobs, repainted in the mural's colours. */
const BLOBS: Record<RimTone, [string, string, string]> = {
  paint: [
    "linear-gradient(90deg, var(--paint-blue), var(--paint-purple), var(--paint-pink))",
    "linear-gradient(90deg, var(--paint-orange), var(--volt-400), var(--paint-pink))",
    "linear-gradient(90deg, var(--paint-teal), var(--paint-blue))",
  ],
  success: [
    "linear-gradient(90deg, var(--paint-teal), #34d399, var(--paint-teal))",
    "linear-gradient(90deg, #6ee7b7, var(--volt-400), #2dd4bf)",
    "linear-gradient(90deg, #047857, var(--paint-teal))",
  ],
  danger: [
    "linear-gradient(90deg, #b5321a, #e11d48, var(--paint-pink))",
    "linear-gradient(90deg, #fb7185, var(--paint-orange), #f87171)",
    "linear-gradient(90deg, #7f1d1d, #dc2626)",
  ],
};

/**
 * The drifting colour under a Cult UI "halo" surface: three blurred blobs
 * sliding along the bottom edge. Sits behind the surface, clipped to its
 * radius; still with reduced motion.
 */
export function HaloRim({
  tone = "paint",
  strength = 0.75,
  className,
}: {
  tone?: RimTone;
  strength?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const [a, b, c] = BLOBS[tone];
  const loop = (duration: number) => ({
    duration: reduce ? 0 : duration,
    repeat: reduce ? 0 : Infinity,
    ease: "easeInOut" as const,
  });
  return (
    <div
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]", className)}
    >
      <div className="absolute inset-x-0 bottom-0 h-[60%]" style={{ opacity: strength }}>
        <motion.div
          className="absolute -bottom-6 h-16 w-48 blur-xl"
          style={{ background: a }}
          animate={{ left: ["-8%", "72%", "-8%"] }}
          transition={loop(6)}
        />
        <motion.div
          className="absolute -bottom-4 h-12 w-32 blur-lg"
          style={{ background: b }}
          animate={{ left: ["64%", "6%", "64%"] }}
          transition={loop(5)}
        />
        <motion.div
          className="absolute -bottom-3 h-10 w-24 blur-lg"
          style={{ background: c }}
          animate={{ left: ["24%", "56%", "24%"] }}
          transition={loop(4)}
        />
      </div>
    </div>
  );
}
